import {
  GTG_DEFAULT_GROUP_ID,
  GEOGRAPHIC_TARGETING_GROUPS_FIELD,
} from "./geographicTargetingGroupConstants";
import { normalizeLocalCarouselGender } from "./localCarouselFacilityConstants";
import {
  clampGtgMaxCpcUsd,
  clampGtgMaxCpmUsd,
  normalizeGtgTargetAudience,
  normalizeGtgType,
  normalizeGtgTargetCountry,
  GTG_TARGET_COUNTRY_US,
  gtgTargetCountryUsesSubdivisionList,
  GTG_TYPE_SYMPTOM_CHECKER,
  parseSubdivisionsForTargetCountry,
} from "@healthdesk/shared-utils";

/**
 * @param {unknown} v
 * @returns {string}
 */
export function newGtgGroupId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `g_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * @param {unknown} data Firestore document data
 * @param {'advertiser'|'facility'} variant
 * @param {(s: unknown) => object} mapSlide from page (includes newSlideId fallback)
 * @returns {{ id: string, zipText: string, countyText: string, targetCountry: string, displayName: string, slides: object[] }[]}
 */
export function loadGeographicTargetingGroupsFromDoc(data, variant, mapSlide) {
  const raw = data?.[GEOGRAPHIC_TARGETING_GROUPS_FIELD];
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((g) => {
      const zips = Array.isArray(g?.zipCodes) ? g.zipCodes : [];
      const ke = Array.isArray(g?.kenyaCounties) ? g.kenyaCounties : [];
      const slidesRaw = Array.isArray(g?.slides) ? g.slides : [];
      const slides =
        slidesRaw.length === 0
          ? [mapSlide(null)]
          : slidesRaw.map((s) => mapSlide(s));
      const displayName =
        variant === "advertiser"
          ? typeof g?.sponsorDisplayName === "string"
            ? g.sponsorDisplayName
            : ""
          : typeof g?.facilityDisplayName === "string"
            ? g.facilityDisplayName
            : "";
      const gtgType = normalizeGtgType(g?.gtgType);
      let targetCountry = normalizeGtgTargetCountry(g?.targetCountry);
      if (gtgType === GTG_TYPE_SYMPTOM_CHECKER) {
        targetCountry = GTG_TARGET_COUNTRY_US;
      }
      const mapCarouselZipCollectionId =
        typeof g?.mapCarouselZipCollectionId === "string" && g.mapCarouselZipCollectionId.trim()
          ? g.mapCarouselZipCollectionId.trim()
          : "";
      return {
        id:
          typeof g?.id === "string" && g.id.trim()
            ? g.id.trim()
            : newGtgGroupId(),
        targetCountry,
        zipText: zips.join("\n"),
        countyText: ke.join("\n"),
        displayName,
        slides,
        targetAudience: normalizeGtgTargetAudience(g?.targetAudience),
        maxCpcUsd: clampGtgMaxCpcUsd(g?.maxCpcUsd),
        maxCpmUsd: clampGtgMaxCpmUsd(g?.maxCpmUsd),
        gtgType,
        mapCarouselZipCollectionId,
      };
    });
  }

  const zips = Array.isArray(data?.zipCodes) ? data.zipCodes : [];
  const keLegacy = Array.isArray(data?.kenyaCounties) ? data.kenyaCounties : [];
  const slidesRaw = Array.isArray(data?.slides) ? data.slides : [];
  const slides =
    slidesRaw.length === 0
      ? [mapSlide(null)]
      : slidesRaw.map((s) => mapSlide(s));
  const displayName =
    variant === "advertiser"
      ? typeof data?.sponsorDisplayName === "string"
        ? data.sponsorDisplayName
        : ""
      : typeof data?.facilityDisplayName === "string"
        ? data.facilityDisplayName
        : "";
  const gtgType = normalizeGtgType(data?.gtgType);
  let targetCountry = normalizeGtgTargetCountry(data?.targetCountry);
  if (gtgType === GTG_TYPE_SYMPTOM_CHECKER) {
    targetCountry = GTG_TARGET_COUNTRY_US;
  }

  const legacyCollectionId =
    typeof data?.mapCarouselZipCollectionId === "string" && data.mapCarouselZipCollectionId.trim()
      ? data.mapCarouselZipCollectionId.trim()
      : "";
  return [
    {
      id: GTG_DEFAULT_GROUP_ID,
      targetCountry,
      zipText: zips.join("\n"),
      countyText: keLegacy.join("\n"),
      displayName,
      slides,
      targetAudience: normalizeGtgTargetAudience(data?.targetAudience),
      maxCpcUsd: clampGtgMaxCpcUsd(data?.maxCpcUsd),
      maxCpmUsd: clampGtgMaxCpmUsd(data?.maxCpmUsd),
      gtgType,
      mapCarouselZipCollectionId: legacyCollectionId,
    },
  ];
}

/**
 * @param {object} group UI group
 * @param {'advertiser'|'facility'} variant
 * @param {(slides: object[]) => object[]} cleanSlidesFn
 */
export function geographicGroupToFirestorePayload(group, variant, cleanSlidesFn) {
  const gtgType = normalizeGtgType(group.gtgType);
  let targetCountry = normalizeGtgTargetCountry(group.targetCountry);
  if (gtgType === GTG_TYPE_SYMPTOM_CHECKER) {
    targetCountry = GTG_TARGET_COUNTRY_US;
  }
  const zipCodes =
    targetCountry === GTG_TARGET_COUNTRY_US
      ? parseZipTextToUniqueZips(group.zipText)
      : [];
  const parsedCounties = gtgTargetCountryUsesSubdivisionList(targetCountry)
    ? parseSubdivisionsForTargetCountry(group.countyText || "", targetCountry)
    : { counties: [], unknown: [] };
  const kenyaCounties = parsedCounties.counties;
  const slides = cleanSlidesFn(group.slides || []);
  const collectionId =
    typeof group.mapCarouselZipCollectionId === "string" && group.mapCarouselZipCollectionId.trim()
      ? group.mapCarouselZipCollectionId.trim()
      : "";
  const base = {
    id: typeof group.id === "string" && group.id.trim() ? group.id.trim() : newGtgGroupId(),
    targetCountry,
    zipCodes,
    kenyaCounties,
    slides,
    targetAudience: normalizeGtgTargetAudience(group.targetAudience),
    maxCpcUsd: clampGtgMaxCpcUsd(group.maxCpcUsd),
    maxCpmUsd: clampGtgMaxCpmUsd(group.maxCpmUsd),
    gtgType,
    ...(collectionId ? { mapCarouselZipCollectionId: collectionId } : {}),
  };
  if (variant === "advertiser") {
    return {
      ...base,
      sponsorDisplayName: (group.displayName || "").trim(),
    };
  }
  return {
    ...base,
    facilityDisplayName: (group.displayName || "").trim(),
  };
}

/**
 * @param {string} text
 * @returns {string[]}
 */
export function parseZipTextToUniqueZips(text) {
  if (typeof text !== "string" || !text.trim()) {
    return [];
  }
  const parts = text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const out = [];
  const seen = new Set();
  for (const p of parts) {
    const digits = p.replace(/\D/g, "");
    if (digits.length >= 5) {
      const z = digits.slice(0, 5);
      if (!seen.has(z)) {
        seen.add(z);
        out.push(z);
      }
    }
  }
  return out;
}

/**
 * @param {object} s slide row
 * @param {() => string} newSlideId
 */
export function mapFirestoreSlideToRow(s, newSlideId) {
  if (!s) {
    return {
      id: newSlideId(),
      message: "",
      buttonLabel: "Learn more",
      href: "/",
      logoUrl: "",
      enabled: true,
      linkToVideo: false,
      learnMoreHref: "",
      patientSegments: { gender: "all" },
    };
  }
  return {
    id:
      typeof s?.id === "string" && s.id.trim()
        ? s.id.trim()
        : newSlideId(),
    message: typeof s?.message === "string" ? s.message : "",
    buttonLabel:
      typeof s?.buttonLabel === "string" ? s.buttonLabel : "Learn more",
    href: typeof s?.href === "string" ? s.href : "/",
    logoUrl: typeof s?.logoUrl === "string" ? s.logoUrl : "",
    enabled: s?.enabled !== false,
    linkToVideo: Boolean(s?.linkToVideo),
    learnMoreHref:
      typeof s?.learnMoreHref === "string" ? s.learnMoreHref.trim() : "",
    patientSegments: {
      gender: normalizeLocalCarouselGender(
        s?.patientSegments?.gender ?? s?.patientGender
      ),
    },
  };
}
